<template>
  <div>
    <!-- Password Reset Form -->
    <validated-form
      v-show="showResetPasswordForm"
      name="sendResetPasswordEmailForm"
      @valid-submit="passwordResestFormSubmitHandler"
    >
      <div class="section center">
        <template v-if="resetPasswordEmailSent">
          <h2 class="title title--2">Success!</h2>

          <p>If we find your email, you will receive an email in the next few minutes to reset your password.</p>

          <button
            type="button"
            class="button button--primary button--auto-width button--large"
            @click="$closeModal('signIn')"
          >
            Okay
          </button>
        </template>

        <template v-else>
          <div class="extra-spacing">
            <h2 class="title title--2">
              Forgot your password?
            </h2>

            <p>Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          <text-input
            v-model="email"
            type="email"
            label="Email"
            name="emailResetPassword"
            :validations="{
              required: true
            }"
          />

          <div class="center">
            <button
              :class="['button button--primary button--large extra-spacing-top', { 'button--loading': resetPasswordIsLoading }]"
              type="submit"
            >
              Send Link
            </button>

            <button
              type="button"
              class="link link--margin"
              @click="closeResetPasswordForm"
            >
              Cancel
            </button>
          </div>
        </template>
      </div>
    </validated-form>

    <!-- Sign In Form -->
    <validated-form
      v-show="!showResetPasswordForm"
      name="signInForm"
      @valid-submit="signInFormSubmitHandler"
    >
      <div class="section center">
        <img
          v-if="signInContent.imageUrl"
          class="image image--logo"
          :alt="merchant.name"
          :src="signInContent.imageUrl"
        >

        <info-message
          v-if="isGiftedPointsClaim"
          class="spacing"
          :message="giftedPointsClaimInfoContent"
          icon="icon far fa-gift"
        />

        <info-message
          v-if="isPromoClaim"
          class="spacing"
          :message="promoClaimInfoContent"
          icon="icon far fa-gift"
        />

        <h2
          v-if="signInContent.title"
          class="title title--2"
        >
          {{ signInContent.title }}
        </h2>

        <p v-if="sessionExpired">
          Your session has expired. Please sign in to continue.
        </p>

        <p v-else>
          {{ signInContent.subtitle }}
        </p>

        <div
          v-if="merchant.allowLoginByPhoneNumber"
          class="tabs tabs--bleed"
        >
          <button
            :class="['tabs__tab', { 'tabs__tab--active': isEmailSignIn }]"
            type="button"
            @click="setSignInMethod('email')"
          >
            Email Address
          </button>
          <button
            :class="['tabs__tab', { 'tabs__tab--active': isPhoneNumberSignIn }]"
            type="button"
            @click="setSignInMethod('phoneNumber')"
          >
            Phone Number
          </button>
        </div>

        <div class="extra-spacing-top">
          <transition
            mode="out-in"
            :name="isPhoneNumberSignIn ? 'slide-to-right-fast' : 'slide-to-left-fast'"
          >
            <text-input
              v-if="isPhoneNumberSignIn"
              key="phone"
              v-model="phoneNumber"
              type="phone"
              label="Phone Number"
              name="phoneNumberSignIn"
              :validations="{ required: true }"
            />
            <text-input
              v-else
              key="email"
              v-model="email"
              type="email"
              label="Email"
              name="emailSignIn"
              :validations="{ required: true }"
            />
          </transition>

          <text-input
            v-model="password"
            type="password"
            label="Password"
            name="passwordSignIn"
            :validations="{ required: true }"
          />
        </div>

        <p class="smaller">
          <button
            class="link"
            type="button"
            @click="openResetPasswordForm"
          >
            Forgot your password?
          </button>
        </p>

        <div class="center">
          <button
            :class="['button button--primary button--large', { 'button--loading': isSigningIn }]"
            type="submit"
          >
            {{ signInContent.buttonCta }}
          </button>
        </div>

        <template v-if="merchant.thirdPartySignIn.enabled">
          <div class="title-separator">
            <p class="title title--6 uppercase">
              or
            </p>
          </div>

          <div class="container max-width-social-sign-in">
            <sign-in-with-google
              v-if="merchant.thirdPartySignIn.google"
              type="sign-in"
            />
            <sign-in-with-facebook v-if="merchant.thirdPartySignIn.facebook" />
          </div>
        </template>
      </div>

      <div class="section section--alt-background section--half-padding center">
        <p>
          Don't have an account?

          <button
            class="link primary"
            type="button"
            @click="openCreateAccountModal"
          >
            Create Account
          </button>
        </p>
      </div>
    </validated-form>
  </div>
</template>



<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';


  export default {
    name: 'SignIn',

    props: {
      sessionExpired: {
        type: Boolean,
        default: false
      },

      onSuccess: {
        type: Function,
        default: null
      }
    },

    data() {
      return {
        email: null,
        isSigningIn: false,
        password: null,
        phoneNumber: null,
        resetPasswordEmailSent: false,
        resetPasswordIsLoading: false,
        signInMethod: 'email',
        showResetPasswordForm: false
      };
    },

    computed: {
      ...mapGetters('content', ['dynamicContent']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['currentUser', 'session']),
      ...mapGetters('offers', ['offers']),

      isGiftedPointsClaim() {
        return !!this.offers.giftingPoints.pointGiftClaimCode;
      },

      giftedPointsClaimInfoContent() {
        const pointsCurrency = this.offers.giftingPoints.currencyPluralName;

        return `Sign in to claim your ${pointsCurrency}!`;
      },

      isPromoClaim() {
        return !!this.offers.promoCode.claimCode;
      },

      promoClaimInfoContent() {
        return 'Sign in to claim your reward!';
      },

      isPhoneNumberSignIn() {
        return this.signInMethod === 'phoneNumber';
      },

      isEmailSignIn() {
        return this.signInMethod === 'email';
      },

      contactInfo() {
        return this.isPhoneNumberSignIn ? this.phoneNumber : this.email;
      },

      signInContent() {
        const { metadata, title } = this.dynamicContent.SignIn;
        const { imageUrl, subtitle, buttonCta } = metadata;

        return { title, imageUrl, subtitle, buttonCta };
      }
    },

    mounted() {
      if (this.offers.giftingPoints.pointGiftClaimCode) {
        this.$store.commit('offers/SET_GIFTING_POINTS_DATA', { claimGiftedPointsFlow: 'SIGNED_IN' });
      }
    },

    methods: {
      setSignInMethod(method) {
        this.signInMethod = method;
      },

      closeResetPasswordForm() {
        this.showResetPasswordForm = false;
      },

      openCreateAccountModal() {
        this.$closeModal('signIn');
        this.$openModal('createAccount');
      },

      openResetPasswordForm() {
        this.resetPasswordEmailSent = false;
        this.showResetPasswordForm = true;
      },

      redirect(route) {
        this.$router.replace({
          name: route.name,
          params: route.params,
          query: route.query
        });
      },

      async passwordResestFormSubmitHandler() {
        try {
          this.resetPasswordIsLoading = true;

          await http.post('/cardfree-api/v1/account/password/reset', {
            emailAddress: this.email
          });

          // this.$triggerGoogleTrackingEvent('ChangePassword', 'Account');

          this.$triggerNewRelicEvent('ChangePassword', 'Information', 'Changed Password');

          this.resetPasswordEmailSent = true;
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.resetPasswordIsLoading = false;
        }
      },

      async signInFormSubmitHandler() {
        this.isSigningIn = true;

        const { error } = await this.$store.dispatch('session/signIn', {
          phoneNumberOrEmail: this.contactInfo,
          password: this.password
        });

        if (error) {
          this.handleError(error);
        }
        else {
          await this.handleSuccess();
        }

        this.isSigningIn = false;
      },

      async handleSuccess() {
        const { signInRedirectRoute } = this.session;

        await this.checkForRetroClaims();

        if (this.onSuccess) await this.onSuccess();
        else if (signInRedirectRoute) this.redirect(signInRedirectRoute);

        this.$closeModal('signIn');

        // this.$triggerGoogleTrackingEvent('SignInSuccess', 'Account');

        this.$triggerNewRelicEvent('SignInSuccess', 'Information', 'Sign In Success');
      },

      async checkForRetroClaims() {
        const { receiptToken } = this.$route.params;

        const retroClaimReceipt = this.offers.retroReceiptToken;

        if (receiptToken || retroClaimReceipt) {
          const dispatchPayload = { type: 'cfReceiptToken', value: receiptToken || retroClaimReceipt };

          await this.$store.dispatch('offers/claimPreviousOrder', dispatchPayload);
        }
      },

      handleError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, this.email, this.phoneNumber);

        if (requiresAuth) {
          const twoFactorModalPayload = { ...rest, onSuccess: this.handleSuccess };

          this.$openModal('twoFactorAuthModal', twoFactorModalPayload, { isRequired: true });
        }

        else {
          errorHandlers.generic(error);

          // this.$triggerGoogleTrackingEvent('SignInFailure', 'Account');

          this.$triggerNewRelicEvent('SignInFailure', 'Error', 'Sign In Failure');
        }
      }
    }
  };
</script>
